import React from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { useMutation, gql } from '@apollo/client';
import Avatar from '../StudentPlatform/shared/Avatar';
import {
  X,
  Mail,
  Phone,
  MapPin,
  GraduationCap,
  Briefcase,
  FileText,
  Video,
  ExternalLink,
  MessageSquare,
  CheckCircle2,
} from 'lucide-react';

const CREATE_CHAT = gql`
  mutation CreateChat($userName: String!) {
    createChat(userName: $userName) {
      id
    }
  }
`;

const StudentProfileModal = ({ isOpen, onClose, student, isMessaging }) => {
  const intl = useIntl();
  const [createChat, { loading }] = useMutation(CREATE_CHAT);

  if (!isOpen || !student) return null;

  const displayName = student.user?.allowed_name || student.user?.name || intl.formatMessage({ id: 'hiring.modal.student_fallback' });
  const avatarUrl = student.avatar_url;
  const isEmployeeCandidate = student.candidate_track === 'employee';

  const handleMessage = async () => {
    try {
      if (isMessaging) {
        window.location.href = '/succession/messages';
        return;
      }

      const result = await createChat({
        variables: { userName: student.user?.user_name },
      });

      if (result.data?.createChat?.id) {
        window.location.href = `/succession/messages?conversation_id=${result.data.createChat.id}`;
      }
    } catch (err) {
      console.error('Failed to create conversation:', err);
      alert(intl.formatMessage({ id: 'hiring.modal.alert.message_error' }));
    }
  };

  return (
    <div className="fixed inset-0 z-50" style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }} onClick={onClose}>
      <div className="absolute inset-0 overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4">
          <div
            className="relative bg-succession-bg-secondary rounded-lg shadow-succession-xl w-full max-w-3xl max-h-[90vh] overflow-hidden border border-succession-border animate-modal-scale-in"
            onClick={(e) => e.stopPropagation()}
          >
          {/* Header */}
          <div className="bg-succession-primary/5 border-b border-succession-border p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-start gap-4 flex-1">
                <Avatar
                  src={avatarUrl}
                  name={displayName}
                  size="xl"
                  className="ring-2 ring-succession-border flex-shrink-0"
                />
                <div className="flex-1">
                  <h2 className="text-2xl font-bold text-succession-text-primary mb-1">
                    {displayName}
                  </h2>
                  {student.school && !isEmployeeCandidate && (
                    <p className="text-succession-text-secondary mb-2">{student.school}</p>
                  )}
                  {student.graduation_year && !isEmployeeCandidate && (
                    <p className="text-sm text-succession-text-secondary">
                      {intl.formatMessage({ id: 'hiring.modal.class_of' }, { year: student.graduation_year })}
                    </p>
                  )}
                  <span className={`inline-flex items-center mt-2 px-2.5 py-1 rounded text-xs font-semibold ${
                    isEmployeeCandidate ? 'bg-indigo-100 text-indigo-700' : 'bg-sky-100 text-sky-700'
                  }`}>
                    {isEmployeeCandidate ? 'Employee Candidate' : 'Student Candidate'}
                  </span>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 rounded-md text-succession-text-secondary hover:text-succession-text-primary hover:bg-succession-bg-primary flex-shrink-0"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Action Button / Conversation Banner */}
            {isMessaging ? (
              <div className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <CheckCircle2 className="w-5 h-5 text-blue-600 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-blue-900">
                    {intl.formatMessage({ id: 'hiring.modal.already_messaging' }, { firstName: displayName.split(' ')[0] })}
                  </p>
                  <p className="text-xs text-blue-700">
                    {intl.formatMessage({ id: 'hiring.modal.continue_in_messages' })}
                  </p>
                </div>
                <button
                  onClick={handleMessage}
                  disabled={loading}
                  className="flex-shrink-0 inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-blue-300 text-sm font-medium text-blue-700 hover:bg-blue-100 transition-colors"
                >
                  <MessageSquare className="w-4 h-4" />
                  {intl.formatMessage({ id: 'hiring.modal.view_chat' })}
                </button>
              </div>
            ) : (
              <button
                onClick={handleMessage}
                disabled={loading}
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md font-medium bg-succession-primary text-white hover:bg-succession-primary-dark transition-colors disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed"
              >
                <MessageSquare className="w-4 h-4" />
                {loading
                  ? intl.formatMessage({ id: 'hiring.modal.starting_conversation' })
                  : intl.formatMessage({ id: 'hiring.modal.send_message' })}
              </button>
            )}
          </div>

          {/* Content */}
          <div className="overflow-y-auto max-h-[calc(90vh-240px)] p-6 space-y-6">
            {/* Contact Information */}
            <div>
              <h3 className="text-lg font-semibold text-succession-text-primary mb-3 flex items-center gap-2">
                <Mail className="w-5 h-5 text-succession-primary" />
                {intl.formatMessage({ id: 'hiring.modal.section.contact' })}
              </h3>
              <div className="grid grid-cols-2 gap-4">
                {student.user?.email && (
                  <div>
                    <p className="text-sm text-succession-text-secondary mb-1">{intl.formatMessage({ id: 'hiring.modal.field.email' })}</p>
                    <p className="text-sm font-medium text-succession-text-primary break-all">
                      {student.user.email}
                    </p>
                  </div>
                )}
                {student.phone && (
                  <div>
                    <p className="text-sm text-succession-text-secondary mb-1">{intl.formatMessage({ id: 'hiring.modal.field.phone' })}</p>
                    <p className="text-sm font-medium text-succession-text-primary">
                      {student.phone}
                    </p>
                  </div>
                )}
                {(student.city || student.state) && (
                  <div className="col-span-2">
                    <p className="text-sm text-succession-text-secondary mb-1">{intl.formatMessage({ id: 'hiring.modal.field.location' })}</p>
                    <p className="text-sm font-medium text-succession-text-primary flex items-center gap-1">
                      <MapPin className="w-4 h-4 text-succession-text-muted" />
                      {[student.city, student.state].filter(Boolean).join(', ')}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Education / Experience */}
            <div>
              <h3 className="text-lg font-semibold text-succession-text-primary mb-3 flex items-center gap-2">
                <GraduationCap className="w-5 h-5 text-succession-primary" />
                {isEmployeeCandidate ? 'Professional Background' : intl.formatMessage({ id: 'hiring.modal.section.education' })}
              </h3>
              <div className="grid grid-cols-2 gap-4">
                {student.school && !isEmployeeCandidate && (
                  <div>
                    <p className="text-sm text-succession-text-secondary mb-1">{intl.formatMessage({ id: 'hiring.modal.field.school' })}</p>
                    <p className="text-sm font-medium text-succession-text-primary">{student.school}</p>
                  </div>
                )}
                {student.degree_program && !isEmployeeCandidate && (
                  <div>
                    <p className="text-sm text-succession-text-secondary mb-1">{intl.formatMessage({ id: 'hiring.modal.field.program' })}</p>
                    <p className="text-sm font-medium text-succession-text-primary">{student.degree_program}</p>
                  </div>
                )}
                {student.graduation_year && !isEmployeeCandidate && (
                  <div>
                    <p className="text-sm text-succession-text-secondary mb-1">{intl.formatMessage({ id: 'hiring.modal.field.graduation_year' })}</p>
                    <p className="text-sm font-medium text-succession-text-primary">{student.graduation_year}</p>
                  </div>
                )}
                {student.employment_type && !isEmployeeCandidate && (
                  <div>
                    <p className="text-sm text-succession-text-secondary mb-1">{intl.formatMessage({ id: 'hiring.modal.field.seeking' })}</p>
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded text-xs font-medium bg-succession-primary/10 text-succession-primary">
                      {student.employment_type === 'internship'
                        ? intl.formatMessage({ id: 'hiring.modal.type.internship' })
                        : intl.formatMessage({ id: 'hiring.modal.type.full_time' })}
                    </span>
                  </div>
                )}
                {isEmployeeCandidate && (
                  <div>
                    <p className="text-sm text-succession-text-secondary mb-1">Experience Level</p>
                    <p className="text-sm font-medium text-succession-text-primary">{student.experience_level || intl.formatMessage({ id: 'hiring.modal.not_provided' })}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Career Interests */}
            {(student.desired_location || student.employment_type || (student.candidate_interests && student.candidate_interests.length > 0)) && (
              <div>
                <h3 className="text-lg font-semibold text-succession-text-primary mb-3 flex items-center gap-2">
                  <Briefcase className="w-5 h-5 text-succession-primary" />
                  {intl.formatMessage({ id: 'hiring.modal.section.career_interests' })}
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  {student.desired_location && (
                    <div>
                      <p className="text-sm text-succession-text-secondary mb-1">{intl.formatMessage({ id: 'hiring.modal.field.preferred_location' })}</p>
                      <p className="text-sm font-medium text-succession-text-primary">{student.desired_location}</p>
                    </div>
                  )}
                  {student.candidate_interests && student.candidate_interests.length > 0 && (
                    <div className="col-span-2">
                      <p className="text-sm text-succession-text-secondary mb-1">{isEmployeeCandidate ? 'Professional Interests' : 'Interests'}</p>
                      <p className="text-sm font-medium text-succession-text-primary">
                        {student.candidate_interests.join(', ')}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Why Financial Services */}
            <div>
              <h3 className="text-lg font-semibold text-succession-text-primary mb-3">
                {isEmployeeCandidate ? 'Why I want to join an SEIC member firm' : intl.formatMessage({ id: 'hiring.modal.section.why_interested' })}
              </h3>
              {student.why_interested ? (
                <p className="text-sm text-succession-text-secondary leading-relaxed">
                  {student.why_interested}
                </p>
              ) : (
                <p className="text-sm text-succession-text-muted italic">{intl.formatMessage({ id: 'hiring.modal.not_provided' })}</p>
              )}
            </div>

            {/* Certifications */}
            {student.certifications && student.certifications.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold text-succession-text-primary mb-3">{intl.formatMessage({ id: 'hiring.modal.section.certifications' })}</h3>
                <div className="flex flex-wrap gap-2">
                  {student.certifications.map((cert, idx) => (
                    <span
                      key={idx}
                      className="px-3 py-1 rounded-md border border-succession-border text-sm text-succession-text-primary bg-succession-bg-primary"
                    >
                      {cert}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Additional Information */}
            {student.family_in_business !== null && student.family_in_business !== undefined && (
              <div>
                <h3 className="text-lg font-semibold text-succession-text-primary mb-3">
                  {intl.formatMessage({ id: 'hiring.modal.section.additional_info' })}
                </h3>
                <div className="flex items-center gap-2">
                  <p className="text-sm text-succession-text-secondary">{intl.formatMessage({ id: 'hiring.modal.field.family_in_business' })}</p>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded text-xs font-medium ${
                    student.family_in_business
                      ? 'bg-green-100 text-green-800'
                      : 'bg-gray-100 text-gray-600'
                  }`}>
                    {student.family_in_business
                      ? intl.formatMessage({ id: 'hiring.modal.yes' })
                      : intl.formatMessage({ id: 'hiring.modal.no' })}
                  </span>
                </div>
              </div>
            )}

            {/* Resume & Video */}
            <div>
              <h3 className="text-lg font-semibold text-succession-text-primary mb-3">{intl.formatMessage({ id: 'hiring.modal.section.resume_video' })}</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-succession-bg-primary border border-succession-border rounded-lg p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      student.resume_url ? 'bg-succession-primary/10 text-succession-primary' : 'bg-gray-100 text-succession-text-muted'
                    }`}>
                      <FileText className="w-5 h-5" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-succession-text-primary">{intl.formatMessage({ id: 'hiring.modal.resume' })}</p>
                      <p className="text-xs text-succession-text-secondary">
                        {student.resume_url
                          ? intl.formatMessage({ id: 'hiring.modal.file.available' })
                          : intl.formatMessage({ id: 'hiring.modal.file.not_uploaded' })}
                      </p>
                    </div>
                  </div>
                  {student.resume_url && (
                    <a
                      href={student.resume_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full inline-flex items-center justify-center gap-1 px-3 py-1.5 rounded-md border border-succession-border text-sm text-succession-text-primary hover:bg-succession-bg-secondary transition-colors"
                    >
                      {intl.formatMessage({ id: 'hiring.modal.file.view_resume' })}
                    </a>
                  )}
                </div>

                <div className="bg-succession-bg-primary border border-succession-border rounded-lg p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      student.video_url ? 'bg-succession-primary/10 text-succession-primary' : 'bg-gray-100 text-succession-text-muted'
                    }`}>
                      <Video className="w-5 h-5" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-succession-text-primary">{intl.formatMessage({ id: 'hiring.modal.video' })}</p>
                      <p className="text-xs text-succession-text-secondary">
                        {student.video_url
                          ? intl.formatMessage({ id: 'hiring.modal.file.available' })
                          : intl.formatMessage({ id: 'hiring.modal.file.not_uploaded' })}
                      </p>
                    </div>
                  </div>
                  {student.video_url && (
                    <a
                      href={student.video_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full inline-flex items-center justify-center gap-1 px-3 py-1.5 rounded-md border border-succession-border text-sm text-succession-text-primary hover:bg-succession-bg-secondary transition-colors"
                    >
                      {intl.formatMessage({ id: 'hiring.modal.file.watch_video' })}
                    </a>
                  )}
                </div>
              </div>
            </div>

            {/* LinkedIn */}
            {student.linkedin_url && (
              <div>
                <a
                  href={student.linkedin_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-md border border-succession-border text-succession-text-primary bg-succession-bg-primary hover:bg-succession-hover-bg transition-colors"
                >
                  <ExternalLink className="w-4 h-4" />
                  {intl.formatMessage({ id: 'hiring.modal.view_linkedin' })}
                </a>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-succession-border p-4 flex items-center justify-between bg-succession-bg-secondary/50">
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-md border border-succession-border text-succession-text-primary hover:bg-succession-bg-primary transition-colors"
            >
              {intl.formatMessage({ id: 'hiring.modal.close' })}
            </button>
            {!isMessaging && (
              <button
                onClick={handleMessage}
                disabled={loading}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-md font-medium bg-succession-primary text-white hover:bg-succession-primary-dark transition-colors disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed"
              >
                <MessageSquare className="w-4 h-4" />
                {loading
                  ? intl.formatMessage({ id: 'hiring.modal.starting' })
                  : intl.formatMessage({ id: 'hiring.modal.send_message' })}
              </button>
            )}
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};

StudentProfileModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  student: PropTypes.object,
  isMessaging: PropTypes.bool,
};

export default StudentProfileModal;
